// One Droplet: what it is, how it has been doing, its snapshots and what the console did to it.
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useCloudDropletQuery } from '@/client/api/cloud/cloud.query';
import { MetricWindow } from '@/client/types/cloud';
import {
  AccessDenied,
  AuditCard,
  CLOUD_BASE,
  Card,
  EmptyRow,
  ErrorNotice,
  FetchingChip,
  Field,
  HealthPill,
  HealthReasons,
  LoadingNote,
  MetricChart,
  PageShell,
  RowMenu,
  Tabs,
  Th,
  UsagePct,
  fmtTime,
} from './cloudUi';

const WINDOWS: readonly MetricWindow[] = ['1h', '6h', '24h', '7d'];
const SPAN_HOURS: Record<MetricWindow, number> = { '1h': 1, '6h': 6, '24h': 24, '7d': 168 };

const CloudDropletPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const viewPerm = useHasPermission('cloud.view');
  const operatePerm = useHasPermission('cloud.operate');
  const managePerm = useHasPermission('cloud.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const [window, setWindow] = useState<MetricWindow>('6h');
  const q = useCloudDropletQuery(id, window, canView);
  const d = q.data;
  useBreadcrumbs([
    { label: 'Cloud', href: CLOUD_BASE },
    { label: 'Resources', href: `${CLOUD_BASE}/resources` },
    { label: d?.server.name ?? 'Droplet' },
  ]);

  if (viewPerm.isLoading || (q.isLoading && !d)) {
    return (
      <PageShell title="Cloud" description="">
        <LoadingNote>Reading this Droplet and its metrics.</LoadingNote>
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Cloud" description="">
        <AccessDenied />
      </PageShell>
    );
  }
  if (!d) {
    return (
      <PageShell title="Cloud" description="">
        <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
      </PageShell>
    );
  }

  const s = d.server;
  const hours = SPAN_HOURS[window];
  return (
    <PageShell
      title={s.name}
      description={['Droplet', s.region, d.about.image].filter(Boolean).join(', ')}
      actions={
        <div className="flex items-center gap-2">
          <FetchingChip />
          <HealthPill level={s.health.level} />
          <RowMenu
            resource={s}
            canOperate={operatePerm.isSuperAdmin || operatePerm.hasPermission}
            canManage={managePerm.isSuperAdmin || managePerm.hasPermission}
          />
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {!d.about.agent && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          This Droplet does not run the monitoring agent, so there are no memory, disk or load
          figures for it and its health is judged on its status alone.
        </p>
      )}

      <Card title="Health">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
          <Field label="Status">
            <HealthPill level={s.health.level} />
            <HealthReasons health={s.health} className="mt-1" />
            <p className="mt-1 text-xs text-gray-500">{s.status}</p>
          </Field>
          <Field label="Memory in use">
            <UsagePct pct={s.metrics?.memory_pct} />
          </Field>
          <Field label="Disk in use">
            <UsagePct pct={s.metrics?.disk_pct} />
          </Field>
          <Field label="Size">
            {s.vcpus} vCPU, {s.memory_mb ? Math.round(s.memory_mb / 1024) : '—'} GB, {s.disk_gb} GB
            disk
            <span className="block text-xs text-gray-500">{s.size}</span>
          </Field>
          <Field label="Address">
            <span className="font-mono text-xs">{s.ip ?? '—'}</span>
          </Field>
          <Field label="Cost and backups">
            {d.about.price_monthly != null ? `$${d.about.price_monthly} a month` : '—'}
            <span className="block text-xs text-gray-500">
              {d.about.backups ? 'automatic backups on' : 'no automatic backups'}
              {d.about.volumes ? `, ${d.about.volumes} volumes` : ''}
            </span>
          </Field>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-800">Metrics</h2>
        <Tabs value={window} onChange={setWindow} options={WINDOWS} />
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card title="CPU %">
          <MetricChart series={d.series.cpu} percent spanHours={hours} />
        </Card>
        <Card title="Memory %" note="dashed line at 85%">
          <MetricChart series={d.series.memory} percent spanHours={hours} />
        </Card>
        <Card title="Disk %" note="root filesystem">
          <MetricChart series={d.series.disk} percent spanHours={hours} />
        </Card>
        <Card title="Load" note="5 minute average">
          <MetricChart series={d.series.load} spanHours={hours} />
        </Card>
      </div>

      <Card title="Snapshots" note="taken of this Droplet" flush>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
              <Th>Name</Th>
              <Th>Taken</Th>
              <Th right>Size</Th>
            </tr>
          </thead>
          <tbody>
            {d.snapshots.length === 0 && (
              <EmptyRow cols={3}>No snapshots. Take one from the menu above.</EmptyRow>
            )}
            {d.snapshots.map((snap) => (
              <tr key={snap.id} className="border-b border-gray-50 last:border-0">
                <td className="px-4 py-2 text-gray-900">{snap.name}</td>
                <td className="px-4 py-2 text-gray-600">{fmtTime(snap.created_at)}</td>
                <td className="px-4 py-2 text-right text-gray-600">
                  {snap.size_gb != null ? `${snap.size_gb} GB` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <AuditCard rows={d.audit} empty="Nothing has been done to this Droplet from the console" />
    </PageShell>
  );
};

export default CloudDropletPage;
